import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { BreadcrumbComponent } from '../breadcrumb/breadcrumb';
import { LanguageSwitcherComponent } from '../language-switcher/language-switcher.component';
import { ResponsiveService } from '../../services/responsive/responsive.service';
import { AnnouncementService } from '../../services/announcement/announcement.service';
import { AuthService } from '../../services/auth/auth.service';
import { WebSocketService } from '../../services/websocket/websocket.service';
import { AnnouncementViewerDialogComponent } from '../announcement-viewer-dialog/announcement-viewer-dialog.component';

@Component({
  selector: 'app-top-navigation',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatDividerModule,
    TranslateModule,
    BreadcrumbComponent,
    LanguageSwitcherComponent,
  ],
  templateUrl: './top-navigation.html',
  styleUrls: ['./top-navigation.scss'],
})
export class TopNavigationComponent implements OnInit, OnDestroy {
  @Input() isLeftNavCollapsed = false;
  @Input() searchPlaceholder = 'Search';
  @Input() showSearch = true;
  @Input() showNotifications = true;
  @Input() notificationCount = 0;
  @Input() userName = 'Vie Jugo';
  @Input() userAvatar = 'VJ';
  @Input() showUserProfile = true;
  @Input() disabled = false;

  showBreadcrumb = true;
  hideUserProfileOnDisable = false;
  unreadAnnouncementCount = 0;
  private destroy$ = new Subject<void>();

  searchValue = '';

  private readonly responsiveService = inject(ResponsiveService);

  isMobile$: Observable<boolean> = this.responsiveService.isMobile$;
  isTablet$: Observable<boolean> = this.responsiveService.isTablet$;
  isMobileOrTablet$: Observable<boolean> = this.responsiveService.currentBreakpoint$.pipe(
    map((breakpoint) => breakpoint === 'mobile' || breakpoint === 'tablet'),
  );
  isDesktop$: Observable<boolean> = this.responsiveService.isDesktop$;

  constructor(
    private router: Router,
    private announcementService: AnnouncementService,
    private authService: AuthService,
    private wsService: WebSocketService,
    private dialog: MatDialog,
  ) {}

  async ngOnInit() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationEnd) => {
        const isDisablePage = event.url.includes('/profile/disable');
        this.showBreadcrumb = !isDisablePage;
        this.hideUserProfileOnDisable = isDisablePage;
      });

    // Only load announcements if authenticated - prevents API calls during redirect to login
    const isAuthenticated = await this.authService.isAuthenticated();
    if (isAuthenticated) {
      this.loadUnreadCount();
      this.wsService.messages
        .pipe(
          filter((msg) => msg.type === 'ANNOUNCEMENT_CREATED'),
          takeUntil(this.destroy$),
        )
        .subscribe(() => this.loadUnreadCount());
    }
  }

  private loadUnreadCount(): void {
    this.announcementService.getUnreadCount().subscribe({
      next: (res) => {
        this.unreadAnnouncementCount = res.count;

        if (!sessionStorage.getItem('announcement_forced_shown') && res.count > 0) {
          sessionStorage.setItem('announcement_forced_shown', 'true');
          this.openAnnouncementDialog();
        }
      },
      error: () => {},
    });
  }

  openAnnouncementDialog(): void {
    const ref = this.dialog.open(AnnouncementViewerDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      data: {},
    });
    ref.afterClosed().subscribe(() => this.loadUnreadCount());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onUserProfileClick() {
    if (this.disabled) return;
    this.router.navigate(['/profile']);
  }

  onLogout() {
    if (this.disabled) return;

    // Use federated logout to chain Cognito → Azure AD logout
    // This ensures both sessions are cleared completely
    this.authService.signOutFederated();
  }
}
